import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { 
  LayoutDashboard, 
  MessageSquare, 
  FolderOpen, 
  Users, 
  Calendar,
  FileText,
  Search,
  Settings,
  Scale,
  PlayCircle,
  Receipt,
  Menu,
  X,
  Megaphone
} from 'lucide-react';
import ApiStatusIndicator from '../ApiStatusIndicator';
import { OfficialSourcesCompact } from '../OfficialSourcesBadge';
import { useNetworkActivity } from '../../hooks/use-network-activity';
import { useAuth } from '../../context/AuthContext';
import { BRAND } from '../../config/branding';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Tableau de bord' },
  { path: '/consultation', icon: PlayCircle, label: 'Consultation', highlight: true },
  { path: '/chat', icon: MessageSquare, label: 'Assistant IA' },
  { path: '/affaires', icon: FolderOpen, label: 'Dossiers' },
  { path: '/clients', icon: Users, label: 'Clients' },
  { path: '/facturation', icon: Receipt, label: 'Facturation' },
  { path: '/calendrier', icon: Calendar, label: 'Calendrier' },
  { path: '/documents', icon: FileText, label: 'Documents' },
  { path: '/recherche', icon: Search, label: 'Recherche' },
];

// Items admin uniquement
const adminNavItems = [
  { path: '/admin/marketing', icon: Megaphone, label: 'Marketing', adminOnly: true },
];

export const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { isActive, isConnected } = useNetworkActivity();
  const { user } = useAuth();
  
  // Combiner les items de navigation (ajouter les items admin si l'utilisateur est admin)
  const allNavItems = user?.role === 'admin' 
    ? [...navItems, ...adminNavItems]
    : navItems;

  // Fermer le menu quand on change de page (mobile)
  useEffect(() => {
    if (onClose && isOpen) onClose();
  }, [location.pathname]);

  return (
    <>
      {/* Overlay pour mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed left-0 top-0 h-screen bg-[#0F172A] border-r border-[#B69D74]/10 flex flex-col z-50 transition-transform duration-300 ease-in-out",
        // Mobile: slide in/out + safe area padding for iOS notch/Dynamic Island
        "w-[280px] -translate-x-full safe-top",
        isOpen && "translate-x-0",
        // Desktop: toujours visible
        "lg:w-64 lg:translate-x-0"
      )}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 lg:px-6 border-b border-[#B69D74]/10">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt={BRAND.name} className="w-8 h-8" />
            <span className="text-lg font-heading font-semibold tracking-tight text-[#F8FAFC]">
              {BRAND.name}
            </span>
          </div>
          {/* Bouton fermer sur mobile */}
          <button 
            onClick={onClose}
            className="lg:hidden p-2 text-[#94A3B8] hover:text-white rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {allNavItems.map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.slice(1).replace('/', '-')}`}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 lg:py-2.5 text-[#94A3B8] transition-all duration-300 rounded-lg lg:rounded-none",
                  isActive && "text-[#B69D74] bg-[#B69D74]/10 lg:border-l-2 lg:border-[#B69D74] lg:-ml-px",
                  !isActive && "hover:text-[#F8FAFC] hover:bg-white/5 active:bg-white/10",
                  item.highlight && !isActive && "border border-[#B69D74]/30 bg-[#B69D74]/5",
                  item.adminOnly && "border-l-2 border-orange-500/50"
                )}
              >
                <item.icon 
                  className={cn(
                    "w-5 h-5 lg:w-4 lg:h-4", 
                    isActive ? "text-[#B69D74]" : item.highlight ? "text-[#B69D74]" : item.adminOnly ? "text-orange-400" : ""
                  )} 
                  strokeWidth={1.5} 
                />
                <span className="text-base lg:text-sm font-medium">{item.label}</span>
                {item.adminOnly && (
                  <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">
                    Admin
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* API Status - Caché sur mobile */}
        <div className="hidden lg:block">
          <ApiStatusIndicator />
        </div>

        {/* Official Sources - Caché sur mobile */}
        <div className="hidden lg:block">
          <OfficialSourcesCompact />
        </div>

        {/* Serveur France Badge - Toujours visible */}
        <div className="px-3 py-2 mt-2">
          <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center gap-2">
              {/* Drapeau français */}
              <div className="flex items-center gap-0.5">
                <div className="w-1.5 h-5 bg-[#0055A4] rounded-l"></div>
                <div className="w-1.5 h-5 bg-white"></div>
                <div className="w-1.5 h-5 bg-[#EF4135] rounded-r"></div>
              </div>
              <span className="text-[11px] text-white/70 font-medium">Serveur France Paris</span>
            </div>
            
            {/* LED d'activité */}
            <div className="flex items-center gap-1">
              {/* LED qui clignote lors de l'activité */}
              <div 
                className={cn(
                  "w-2 h-2 rounded-full transition-all duration-200",
                  isActive && "animate-pulse shadow-lg",
                  isConnected ? "bg-green-500 shadow-green-500/50" : "bg-red-500 shadow-red-500/50"
                )}
                title={isConnected ? "Connecté au backend" : "Déconnecté"}
              >
                {/* Glow effect quand actif */}
                {isActive && (
                  <div className="absolute w-2 h-2 bg-green-400 rounded-full animate-ping opacity-75"></div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Settings */}
        <div className="p-3 border-t border-[#B69D74]/10">
          <Link
            to="/settings"
            data-testid="nav-settings"
            className={cn(
              "flex items-center gap-3 px-4 py-3 lg:py-2.5 text-[#94A3B8] transition-all duration-300 rounded-lg lg:rounded-none",
              location.pathname === '/settings' && "text-[#B69D74] bg-[#B69D74]/10 lg:border-l-2 lg:border-[#B69D74] lg:-ml-px",
              location.pathname !== '/settings' && "hover:text-[#F8FAFC] hover:bg-white/5 active:bg-white/10"
            )}
          >
            <Settings className="w-5 h-5 lg:w-4 lg:h-4" strokeWidth={1.5} />
            <span className="text-base lg:text-sm font-medium">Paramètres</span>
          </Link>
        </div>
      </aside>
    </>
  );
};

// Bouton hamburger exporté pour le Header
export const MobileMenuButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="lg:hidden p-2 -ml-2 text-[#94A3B8] hover:text-white hover:bg-white/5 rounded-lg transition-colors"
    aria-label="Ouvrir le menu"
  >
    <Menu className="w-6 h-6" />
  </button>
);
